import { browser } from "$app/environment";
import { persisted } from "./persisted.svelte.ts";

export type Theme = {
  preference: "light" | "dark" | "system";
  current: "light" | "dark";
};

/**
 * This is the theme preference of the user, which can be light, dark or system.
 * If preference === 'system', then any device-level theme change will affect the site also.
 * if preference !== 'system', then the theme will be fixed to the preference.
 * To sync the theme with the system, we can either watch theme.current to be the same as system-theme, or allow user to pick "System" from a theme switcher
 */
export const theme = persisted<Theme>("findai:theme", {
  preference: "system",
  current: browser ? (matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light") : "light",
});

/**
 * Initialized in +layout.svelte file, this watches the system preference for any change, and applies the theme accordingly.
 */
export function createThemeWatcher() {
  // This initializes the water on theme, to apply the classes to body
  $effect(() => {
    if (!browser) return;

    document.body.classList.remove("light", "dark");
    document.body.classList.add(theme.current);
  });

  // Now watch media query for prefer-dark-mode, and based on that, change the value of theme.current
  $effect(() => {
    if (!browser) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const controller = new AbortController();
    mediaQuery.addEventListener(
      "change",
      (e) => {
        if (theme.preference === "system") {
          theme.current = e.matches ? "dark" : "light";
        }
      },
      { signal: controller.signal },
    );

    return () => {
      controller.abort();
    };
  });
}
